import React from 'react';
import { Box, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { QcDiagnosisCodeDto, QcDiagnosticWorkupDto } from '../../../services/qualityCheck';
import { formatDiagnosisCode } from '../../../utils/icd11Display';

interface DiagnosticWorkupProps {
    workup: QcDiagnosticWorkupDto;
}

/**
 * `1F54.1 — Cutaneous leishmaniasis`: the API names every code it can (SKIN-612), and the
 * code is shown bare only when nothing names it. Same format as every other ICD-11 surface.
 */
const formatCode = ({ code, label }: QcDiagnosisCodeDto): string => formatDiagnosisCode(code, label);

/**
 * The questions, in the order a reviewer checks them: the verdict first, then what
 * supports it. `key` names both the translation and the test id.
 */
const ROWS: ReadonlyArray<{ key: string; value: (w: QcDiagnosticWorkupDto) => string[] }> = [
    { key: 'final', value: (w) => w.finalDiagnoses.map(formatCode) },
    { key: 'clinical', value: (w) => w.clinicalDiagnoses.map(formatCode) },
    { key: 'differential', value: (w) => w.differentialDiagnoses.map(formatCode) },
    { key: 'confidence', value: (w) => (w.confidence ? [w.confidence] : []) },
    { key: 'other-text', value: (w) => (w.otherDiagnosisText ? [w.otherDiagnosisText] : []) },
    { key: 'lab-performed', value: (w) => (w.labTestPerformed ? [w.labTestPerformed] : []) },
    { key: 'lab-type', value: (w) => (w.labTestType ? [w.labTestType] : []) },
    { key: 'lab-result', value: (w) => w.labResultDiagnoses.map(formatCode) },
    { key: 'lab-id', value: (w) => (w.labId ? [w.labId] : []) },
];

/**
 * SKIN-611 — one diagnostic workup, read-only: what the eCRF says a lesion (or, on a legacy
 * visit, the visit) was diagnosed with, and the lab answers behind it.
 *
 * Only answered questions are listed. A lesion with no lab test has no lab result to show,
 * and a blank row would read as a missing answer rather than one that does not apply.
 * There is no control here: correcting a diagnosis goes through the correction flag.
 */
export default function DiagnosticWorkup({ workup }: DiagnosticWorkupProps) {
    const rows = ROWS
        .map((row) => ({ key: row.key, values: row.value(workup) }))
        .filter((row) => row.values.length > 0);

    return (
        <Box
            component="dl"
            data-testid="diagnostic-workup"
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'minmax(140px, max-content) 1fr' },
                columnGap: 2,
                rowGap: 0.5,
                m: 0,
            }}
        >
            {rows.map(({ key, values }) => (
                <React.Fragment key={key}>
                    <Typography component="dt" variant="body2" color="text.secondary">
                        <Translate id={`hospital.quality-check.review.workup.${key}`} />
                    </Typography>
                    <Typography component="dd" variant="body2" sx={{ m: 0 }} data-testid={`workup-${key}`}>
                        {values.join('; ')}
                    </Typography>
                </React.Fragment>
            ))}
        </Box>
    );
}
